import { Routes, Route, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, ShieldCheck, Users, Building2, FileSpreadsheet, ClipboardList, Calendar, Download, Archive } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { api, formatApiError, downloadBlob } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

const BASE_NAV = [
  { to: "/administration", label: "Tableau de bord", end: true },
  { to: "/administration/utilisateurs", label: "Utilisateurs" },
  { to: "/administration/centres", label: "Centres" },
  { to: "/administration/dossiers", label: "Dossiers & RDV" },
  { to: "/administration/faq", label: "FAQ" },
  { to: "/administration/blog", label: "Blog" },
  { to: "/administration/rgpd", label: "Demandes RGPD" },
  { to: "/administration/logs", label: "Logs" },
];

function useAdminNav() {
  const { user } = useAuth();
  if (user?.role === "superadmin") {
    const nav = [...BASE_NAV];
    // Insert Réseaux + Export clients for super-admin
    nav.splice(3, 0,
      { to: "/administration/reseaux", label: "Réseaux" },
      { to: "/administration/export-clients", label: "Export clients" },
    );
    return nav;
  }
  return BASE_NAV;
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 card-lux">
      <div className="w-10 h-10 rounded-lg bg-sky-blue flex items-center justify-center"><Icon size={20} className="text-primary-blue" /></div>
      <div className="mt-4 text-slate-500 text-sm">{label}</div>
      <div className="text-3xl font-heading text-primary-blue mt-1">{value}</div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/dashboard").then((r) => setStats(r.data.stats || {})); }, []);
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Tableau de bord</h1>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat icon={ClipboardList} label="Nouvelles demandes" value={stats.new_requests ?? 0} />
        <Stat icon={CheckCircle2} label="Réponses des centres" value={stats.center_responses ?? 0} />
        <Stat icon={Calendar} label="À confirmer" value={stats.pending_confirmation ?? 0} />
        <Stat icon={ShieldCheck} label="Confirmés" value={stats.confirmed ?? 0} />
        <Stat icon={Users} label="Utilisateurs" value={stats.users ?? 0} />
        <Stat icon={Building2} label="Centres actifs" value={stats.active_centers ?? 0} />
        <Stat icon={FileSpreadsheet} label="Demandes RGPD" value={stats.rgpd_open ?? 0} />
      </div>
    </div>
  );
}

function UsersPage() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", role: "admin", phone: "", center_id: "" });
  const [centers, setCenters] = useState([]);

  const fetchUsers = () => {
    const params = {};
    if (q) params.q = q;
    if (role !== "all") params.role = role;
    api.get("/admin/users", { params }).then((r) => setUsers(r.data.users || []));
  };
  useEffect(() => { fetchUsers(); api.get("/centers", { params: { limit: 200 } }).then((r) => setCenters(r.data.centers || [])); }, []);
  useEffect(() => { fetchUsers(); }, [role]);

  const createUser = async () => {
    if (!form.email || !form.password || form.password.length < 8) return toast.error("Champs requis / mot de passe 8+");
    try {
      const payload = { ...form };
      if (form.role !== "center") delete payload.center_id;
      await api.post("/admin/users", payload);
      toast.success("Utilisateur créé");
      setShowCreate(false);
      setForm({ email: "", password: "", first_name: "", last_name: "", role: "admin", phone: "", center_id: "" });
      fetchUsers();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { blocked: !u.blocked });
      toast.success(u.blocked ? "Débloqué" : "Bloqué");
      fetchUsers();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadUserConsent = async (u) => {
    try {
      await downloadBlob(`/admin/users/${u.id}/consent-pdf`);
      toast.success("Téléchargement lancé");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  const submitDeleteUser = async () => {
    if (!deleteReason.trim()) return toast.error("Motif obligatoire");
    try {
      const r = await api.delete(`/superadmin/users/${deleteUser.id}`, { data: { reason: deleteReason } });
      toast.success(r.data.kept_data ? "Utilisateur anonymisé (données conservées pour dossiers)" : "Utilisateur anonymisé");
      setDeleteUser(null); setDeleteReason(""); fetchUsers();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <h1 className="font-heading text-3xl text-primary-blue">Utilisateurs</h1>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-primary-blue text-white" data-testid="admin-users-new">Nouvel utilisateur</Button>
      </div>

      {showCreate && (
        <div className="mt-4 bg-white p-6 rounded-2xl border border-slate-200 card-lux grid md:grid-cols-2 gap-4">
          <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-11" /></div>
          <div>
            <Label>Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="center">Centre partenaire</SelectItem>
                <SelectItem value="admin">Administrateur</SelectItem>
                <SelectItem value="superadmin">Super-administrateur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "center" && (
            <div>
              <Label>Centre associé</Label>
              <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v })}>
                <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.city}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="md:col-span-2"><Button onClick={createUser} className="bg-primary-blue text-white" data-testid="admin-users-create-submit">Créer</Button></div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Recherche par email / nom" className="max-w-sm h-10" />
        <Button onClick={fetchUsers} variant="outline">Rechercher</Button>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-48 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous rôles</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="center">Centres</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
            <SelectItem value="superadmin">Super-admins</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Nom</th>
              <th className="text-left px-4 py-3">E-mail</th>
              <th className="text-left px-4 py-3">Rôle</th>
              <th className="text-left px-4 py-3">Créé le</th>
              <th className="text-left px-4 py-3">Dernière connexion</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-${u.id}`}>
                <td className="px-4 py-3">{u.first_name} {u.last_name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3"><Badge variant="outline">{u.role}</Badge></td>
                <td className="px-4 py-3 text-slate-500">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-slate-500">{u.last_login_at ? u.last_login_at.slice(0, 10) : "—"}</td>
                <td className="px-4 py-3">{u.blocked ? <Badge className="bg-red-100 text-red-700">Bloqué</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => toggleBlock(u)}>{u.blocked ? "Débloquer" : "Bloquer"}</Button>
                    {u.role === "client" && (
                      u.consent_status === "OK" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-primary-blue text-primary-blue"
                          onClick={() => downloadUserConsent(u)}
                          data-testid={`admin-user-consent-${u.id}`}
                        >
                          <Download size={14} className="mr-1" /> Fiche consentement
                        </Button>
                      ) : u.consent_status === "ERROR" ? (
                        <Badge className="bg-red-100 text-red-700 border-red-200" data-testid={`admin-user-consent-error-${u.id}`}>
                          PDF en erreur
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`admin-user-consent-pending-${u.id}`}>
                          Consentement à renouveler
                        </Badge>
                      )
                    )}
                    {isSuperAdmin && u.role !== "superadmin" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700"
                        onClick={() => { setDeleteUser(u); setDeleteReason(""); }}
                        data-testid={`admin-user-delete-${u.id}`}
                      >
                        Supprimer
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deleteUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-2xl max-w-md w-full">
            <h3 className="font-heading text-xl text-primary-blue">Supprimer l'utilisateur</h3>
            <p className="text-sm text-slate-600 mt-2">
              L'utilisateur <strong>{deleteUser.email || deleteUser.phone}</strong> sera anonymisé. Ses dossiers, rendez-vous et consentements sont conservés pour ne pas casser l'historique.
            </p>
            <Label className="text-sm mt-4 block">Motif obligatoire</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3} className="mt-1" data-testid="admin-user-delete-reason" />
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeleteUser(null)}>Annuler</Button>
              <Button onClick={submitDeleteUser} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-user-delete-confirm">Confirmer la suppression</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Centers() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "superadmin";
  const [centers, setCenters] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", network_slug: "", address: "", postal_code: "", city: "", phone: "", email: "", hours: "", website: "", services: "", active: true });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterNetwork, setFilterNetwork] = useState("all");
  const [deleteCenter, setDeleteCenter] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  const fetch = () => api.get("/centers", { params: { active_only: false, limit: 500 } }).then((r) => setCenters(r.data.centers || []));
  useEffect(() => { fetch(); api.get("/networks").then((r) => setNetworks(r.data.networks || [])); }, []);

  const filtered = centers.filter((c) => {
    if (filterNetwork !== "all" && c.network_slug !== filterNetwork) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s)
        || (c.city || "").toLowerCase().includes(s)
        || (c.postal_code || "").includes(s)
        || (c.network_slug || "").toLowerCase().includes(s);
  });

  const submitDeleteCenter = async () => {
    if (!deleteReason.trim()) return toast.error("Motif obligatoire");
    try {
      const r = await api.delete(`/superadmin/centers/${deleteCenter.id}`, { data: { reason: deleteReason } });
      toast.success(r.data.kept_history ? "Centre archivé (historique conservé)" : "Centre archivé");
      setDeleteCenter(null); setDeleteReason(""); fetch();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    const payload = { ...form, services: form.services ? form.services.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (editing) {
        await api.patch(`/admin/centers/${editing}`, payload);
        toast.success("Centre modifié");
      } else {
        await api.post("/admin/centers", payload);
        toast.success("Centre créé");
      }
      setShowForm(false); setEditing(null); fetch();
      setForm({ name: "", network_slug: "", address: "", postal_code: "", city: "", phone: "", email: "", hours: "", website: "", services: "", active: true });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archiver ce centre ?")) return;
    try { await api.delete(`/admin/centers/${id}`); toast.success("Archivé"); fetch(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (c) => {
    setEditing(c.id);
    setForm({ ...c, services: (c.services || []).join(", ") });
    setShowForm(true);
  };
  const toggleActive = async (c) => {
    try { await api.patch(`/admin/centers/${c.id}`, { active: !c.active }); fetch(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-heading text-3xl text-primary-blue">Centres</h1>
        <Button onClick={() => { setShowForm(!showForm); setEditing(null); }} className="bg-primary-blue text-white" data-testid="admin-centers-new">Nouveau centre</Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <Input
          placeholder="Rechercher un centre (nom, ville, code postal, réseau)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-11 max-w-md flex-1"
          data-testid="admin-centers-search"
        />
        <Select value={filterNetwork} onValueChange={setFilterNetwork}>
          <SelectTrigger className="h-11 w-64" data-testid="admin-centers-filter-network">
            <SelectValue placeholder="Filtrer par réseau" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les réseaux</SelectItem>
            {networks.map((n) => <SelectItem key={n.slug} value={n.slug}>{n.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{filtered.length} centre(s)</span>
      </div>
      {showForm && (
        <div className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux grid md:grid-cols-2 gap-4">
          <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" /></div>
          <div>
            <Label>Réseau</Label>
            <Select value={form.network_slug} onValueChange={(v) => setForm({ ...form, network_slug: v })}>
              <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{networks.map((n) => <SelectItem key={n.slug} value={n.slug}>{n.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" /></div>
          <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
          <div className="md:col-span-2"><Label>Horaires</Label><Input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="mt-1 h-11" /></div>
          <div className="md:col-span-2"><Label>Site officiel</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 h-11" /></div>
          <div className="md:col-span-2"><Label>Services (séparés par des virgules)</Label><Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} className="mt-1 h-11" /></div>
          <div className="md:col-span-2"><Button onClick={submit} className="bg-primary-blue text-white" data-testid="admin-centers-save">{editing ? "Enregistrer" : "Créer"}</Button></div>
        </div>
      )}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Nom</th>
              <th className="text-left px-4 py-3">Réseau</th>
              <th className="text-left px-4 py-3">Ville</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3">{c.network_slug}</td>
                <td className="px-4 py-3">{c.postal_code} {c.city}</td>
                <td className="px-4 py-3">{c.active ? <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge> : <Badge className="bg-slate-100 text-slate-500">Archivé</Badge>}</td>
                <td className="px-4 py-3 space-x-2">
                  <Button size="sm" variant="outline" onClick={() => edit(c)}>Modifier</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(c)}>{c.active ? "Désactiver" : "Réactiver"}</Button>
                  <Button size="sm" variant="outline" className="text-red-700" onClick={() => archive(c.id)}>Archiver</Button>
                  {isSuperAdmin && (
                    <Button size="sm" variant="outline" className="border-red-400 text-red-700" onClick={() => { setDeleteCenter(c); setDeleteReason(""); }} data-testid={`admin-center-delete-${c.id}`}>
                      Supprimer
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deleteCenter && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-2xl max-w-md w-full">
            <h3 className="font-heading text-xl text-primary-blue">Supprimer le centre</h3>
            <p className="text-sm text-slate-600 mt-2">
              Le centre <strong>{deleteCenter.name}</strong> sera archivé. Il ne pourra plus être sélectionné dans les formulaires. Les dossiers et rendez-vous existants restent consultables.
            </p>
            <Label className="text-sm mt-4 block">Motif obligatoire</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3} className="mt-1" data-testid="admin-center-delete-reason" />
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeleteCenter(null)}>Annuler</Button>
              <Button onClick={submitDeleteCenter} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-center-delete-confirm">Confirmer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Imports() {
  const fileRef = useRef();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetch = () => api.get("/admin/imports").then((r) => setHistory(r.data.imports || []));
  useEffect(() => { fetch(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Sélectionnez un fichier");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/centers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data.report);
      toast.success("Import effectué");
      fetch();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Import de centres (CSV / XLSX)</h1>
      <div className="mt-4 bg-white p-6 rounded-2xl border border-slate-200 card-lux">
        <div className="text-slate-600 text-sm mb-3">
          Colonnes acceptées : <code>network_slug, name, address, postal_code, city, phone, email, hours, website, services</code> (services séparés par <code>|</code>).
        </div>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" data-testid="admin-import-file" />
        <div className="mt-4"><Button onClick={upload} disabled={busy} className="bg-primary-blue text-white" data-testid="admin-import-submit"><Upload size={16} className="mr-2" />{busy ? "Import…" : "Importer"}</Button></div>
      </div>
      {report && (
        <div className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux">
          <h2 className="font-heading text-xl text-primary-blue">Rapport d'import</h2>
          <div className="mt-2 grid sm:grid-cols-4 gap-4 text-sm">
            <div>Total lignes : <strong>{report.total}</strong></div>
            <div>Créés : <strong>{report.created}</strong></div>
            <div>Mis à jour : <strong>{report.updated}</strong></div>
            <div>Doublons : <strong>{report.duplicates}</strong></div>
          </div>
          {report.errors?.length > 0 && (
            <div className="mt-4 text-sm text-red-700">
              <div className="font-semibold">Erreurs :</div>
              <ul className="list-disc pl-5">{report.errors.slice(0, 20).map((e) => <li key={`row-${e.row}-${e.reason}`}>Ligne {e.row} : {e.reason}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      <h2 className="mt-8 font-heading text-2xl text-primary-blue">Historique des imports</h2>
      <div className="mt-3 space-y-3">
        {history.map((h) => (
          <div key={h.id} className="bg-white p-4 rounded-xl border border-slate-200 text-sm">
            <div className="flex justify-between"><strong>{h.filename}</strong><span className="text-slate-500">{h.created_at?.slice(0, 16).replace("T", " ")}</span></div>
            <div className="text-slate-600 mt-1">Créés : {h.report?.created} · MàJ : {h.report?.updated} · Doublons : {h.report?.duplicates}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dossiers() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [centers, setCenters] = useState([]);
  const [reassignId, setReassignId] = useState("");
  const [selectedCenter, setSelectedCenter] = useState("");

  const fetch = () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    api.get("/admin/appointments", { params }).then((r) => setItems(r.data.appointments || []));
  };
  useEffect(() => { fetch(); api.get("/centers", { params: { limit: 500 } }).then((r) => setCenters(r.data.centers || [])); }, []);
  useEffect(() => { fetch(); }, [status]);

  const setStatusAction = async (id, s) => {
    try { await api.post(`/appointments/${id}/status`, { status: s, comment: `Admin -> ${s}` }); toast.success("Statut mis à jour"); fetch(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const assign = async () => {
    if (!selectedCenter || !reassignId) return;
    try { await api.post(`/appointments/${reassignId}/assign`, { center_id: selectedCenter }); toast.success("Assigné"); setReassignId(""); setSelectedCenter(""); fetch(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadApptConsent = async (a) => {
    try {
      await downloadBlob(`/admin/appointments/${a.id}/consent-pdf`);
      toast.success("Téléchargement lancé");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Dossiers & rendez-vous</h1>
      <div className="mt-4 flex flex-wrap gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Réf / email / nom" className="max-w-sm h-10" />
        <Button onClick={fetch} variant="outline">Rechercher</Button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-64 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {["NEW_REQUEST", "ASSIGNED_TO_CENTER", "WAITING_CENTER_RESPONSE", "CENTER_AVAILABLE", "ALTERNATIVE_DATE_PROPOSED", "CENTER_UNAVAILABLE", "PENDING_ADMIN_CONFIRMATION", "CONFIRMED", "CANCELLED", "COMPLETED", "NO_SHOW", "CLOSED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-6 space-y-4">
        {items.map((a) => (
          <div key={a.id} className="bg-white p-6 rounded-2xl border border-slate-200 card-lux" data-testid={`admin-appt-${a.id}`}>
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <div className="text-sm text-slate-500">Réf. {a.reference} · {a.created_at?.slice(0, 10)}</div>
                <div className="mt-1 font-heading text-lg text-primary-blue">{a.client.first_name} {a.client.last_name} — {a.reason}</div>
                <div className="text-sm text-slate-600">{a.client.email} · {a.client.phone} · {a.preferred_date} ({a.time_preference})</div>
                <div className="text-xs text-slate-500 mt-1">Réseau : {a.network_slug} · Centre : {a.center_id}</div>
              </div>
              <StatusBadge status={a.status} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {a.status !== "CONFIRMED" && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setStatusAction(a.id, "CONFIRMED")} data-testid={`admin-confirm-${a.id}`}>Confirmer le RDV</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setStatusAction(a.id, "CANCELLED")}>Annuler</Button>
              <Button size="sm" variant="outline" onClick={() => setStatusAction(a.id, "COMPLETED")}>Marquer réalisé</Button>
              <Button size="sm" variant="outline" onClick={() => setReassignId(a.id)}>Réassigner</Button>
              <Button
                size="sm"
                variant="outline"
                className="border-primary-blue text-primary-blue"
                onClick={() => downloadApptConsent(a)}
                data-testid={`admin-appt-consent-${a.id}`}
              >
                <Download size={14} className="mr-1" /> Fiche consentement
              </Button>
            </div>
            {reassignId === a.id && (
              <div className="mt-3 flex flex-wrap gap-2 items-end">
                <div>
                  <Label className="text-xs">Nouveau centre</Label>
                  <Select value={selectedCenter} onValueChange={setSelectedCenter}>
                    <SelectTrigger className="w-72 h-10"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent className="max-h-72">{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.city}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={assign} className="bg-primary-blue text-white">Assigner</Button>
                <Button variant="outline" onClick={() => setReassignId("")}>Annuler</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FAQAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", category: "Général", order: 0, published: true });
  const fetch = () => api.get("/faq").then((r) => setItems(r.data.faqs || []));
  useEffect(() => { fetch(); }, []);
  const add = async () => {
    try { await api.post("/admin/faq", form); toast.success("FAQ ajoutée"); setForm({ question: "", answer: "", category: "Général", order: 0, published: true }); fetch(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/faq/${id}`); fetch(); };
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">FAQ</h1>
      <div className="mt-4 bg-white p-6 rounded-2xl border border-slate-200 card-lux grid gap-3">
        <Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Question" data-testid="admin-faq-q" />
        <Textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Réponse" rows={3} data-testid="admin-faq-a" />
        <div className="flex gap-3">
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Catégorie" className="max-w-xs" />
          <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} placeholder="Ordre" className="max-w-xs" />
        </div>
        <div><Button onClick={add} className="bg-primary-blue text-white" data-testid="admin-faq-add">Ajouter</Button></div>
      </div>
      <div className="mt-6 space-y-3">
        {items.map((f) => (
          <div key={f.id} className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-start gap-4">
            <div>
              <div className="text-xs text-slate-500">{f.category} · #{f.order}</div>
              <div className="font-medium text-slate-800">{f.question}</div>
              <div className="text-sm text-slate-600 mt-1">{f.answer}</div>
            </div>
            <Button size="sm" variant="outline" className="text-red-700" onClick={() => del(f.id)}>Supprimer</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", summary: "", content: "", image_url: "", published: true });
  const fetch = () => api.get("/blog", { params: { published_only: false } }).then((r) => setItems(r.data.posts || []));
  useEffect(() => { fetch(); }, []);
  const add = async () => {
    try { await api.post("/admin/blog", form); toast.success("Article publié"); setForm({ title: "", summary: "", content: "", image_url: "", published: true }); fetch(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/blog/${id}`); fetch(); };
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Blog</h1>
      <div className="mt-4 bg-white p-6 rounded-2xl border border-slate-200 card-lux grid gap-3">
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" data-testid="admin-blog-title" />
        <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Résumé" />
        <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Contenu" rows={6} />
        <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL image (optionnel)" />
        <div><Button onClick={add} className="bg-primary-blue text-white" data-testid="admin-blog-add">Publier</Button></div>
      </div>
      <div className="mt-6 space-y-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-start gap-4">
            <div>
              <div className="font-medium text-slate-800">{p.title}</div>
              <div className="text-sm text-slate-500">/{p.slug} · {p.published ? "publié" : "brouillon"}</div>
            </div>
            <div className="flex gap-2">
              <Link to={`/blog/${p.slug}`} target="_blank"><Button size="sm" variant="outline">Voir</Button></Link>
              <Button size="sm" variant="outline" className="text-red-700" onClick={() => del(p.id)}>Supprimer</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RGPD() {
  const [items, setItems] = useState([]);
  const fetch = () => api.get("/admin/rgpd").then((r) => setItems(r.data.requests || []));
  useEffect(() => { fetch(); }, []);
  const update = async (id, status) => { await api.patch(`/admin/rgpd/${id}`, { status }); toast.success("Mis à jour"); fetch(); };
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Demandes RGPD</h1>
      <div className="mt-6 space-y-3">
        {items.map((r) => (
          <div key={r.id} className="bg-white p-4 rounded-xl border border-slate-200">
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div>
                <div className="text-sm text-slate-500">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
                <div className="font-medium">{r.email} — {r.request_type}</div>
                <div className="text-sm text-slate-600 mt-1">{r.message}</div>
              </div>
              <Badge variant="outline">{r.status}</Badge>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => update(r.id, "PROCESSING")}>Traiter</Button>
              <Button size="sm" variant="outline" onClick={() => update(r.id, "DONE")}>Résolu</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500">Aucune demande.</div>}
      </div>
    </div>
  );
}

function Logs() {
  const [logins, setLogins] = useState([]);
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    api.get("/admin/login-logs").then((r) => setLogins(r.data.logs || []));
    api.get("/admin/audit-logs").then((r) => setAudit(r.data.logs || []));
  }, []);
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Journaux</h1>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div>
          <h2 className="font-heading text-lg text-primary-blue mb-2">Logs de connexion</h2>
          <div className="bg-white rounded-xl border border-slate-200 max-h-[500px] overflow-y-auto text-sm">
            {logins.map((l) => (
              <div key={l.id} className="px-4 py-2 border-b border-slate-100 flex justify-between">
                <span>{l.email}</span>
                <span className={l.success ? "text-emerald-600" : "text-red-600"}>{l.success ? "OK" : "Échec"} · {l.ip}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-heading text-lg text-primary-blue mb-2">Journal d'audit</h2>
          <div className="bg-white rounded-xl border border-slate-200 max-h-[500px] overflow-y-auto text-sm">
            {audit.map((l) => (
              <div key={l.id} className="px-4 py-2 border-b border-slate-100">
                <div className="flex justify-between"><strong>{l.action}</strong><span className="text-slate-500">{l.created_at?.slice(0, 16).replace("T", " ")}</span></div>
                <div className="text-slate-600 text-xs">{l.actor_role} · {l.target}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportClients() {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      await downloadBlob("/superadmin/clients-export");
      toast.success("Archive téléchargée. Vérifiez erreurs.txt en cas d'incident.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Export clients</h1>
      <p className="mt-2 text-slate-600">
        Exporte tous les comptes clients ainsi que leurs fiches de consentement
        dans une archive ZIP. Fonction réservée au super-administrateur.
      </p>
      <div className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux">
        <h2 className="font-heading text-xl text-primary-blue">Contenu de l'archive</h2>
        <ul className="mt-3 text-slate-700 text-sm space-y-1 list-disc pl-6">
          <li><code>clients.csv</code> — liste complète avec id, référence, coordonnées, statut, référence & date de consentement, présence PDF.</li>
          <li><code>consentements/</code> — un PDF par client au format <code>consentement_REFERENCE_NOM_PRENOM.pdf</code>.</li>
          <li><code>erreurs.txt</code> — présent uniquement si certains PDF manquent, avec le détail par client.</li>
        </ul>
        <div className="mt-6 text-sm text-slate-600 p-4 bg-sky-blue/40 rounded-lg border border-primary-blue/10 flex items-start gap-2">
          <ShieldCheck size={18} className="text-primary-blue shrink-0 mt-0.5" />
          <span>
            Aucun mot de passe, token, adresse IP ou log technique n'est inclus.
            L'archive est générée côté serveur, transmise directement au navigateur,
            puis supprimée automatiquement du disque.
          </span>
        </div>
        {!confirmOpen ? (
          <div className="mt-6">
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
              className="bg-primary-blue text-white hover:bg-[#0a1d36]"
              data-testid="export-clients-btn"
            >
              <Archive size={16} className="mr-2" />
              Exporter tous les clients et consentements
            </Button>
          </div>
        ) : (
          <div className="mt-6 p-5 rounded-lg bg-amber-50 border border-amber-200">
            <div className="font-medium text-amber-900">Confirmation requise</div>
            <div className="text-sm text-amber-900/80 mt-1">
              Vous êtes sur le point d'exporter l'intégralité des clients et leurs fiches
              de consentement. Cette action est journalisée. Confirmer ?
            </div>
            <div className="mt-4 flex gap-3">
              <Button onClick={run} disabled={busy} className="bg-primary-blue text-white" data-testid="export-clients-confirm">
                {busy ? "Génération en cours…" : "Oui, exporter maintenant"}
              </Button>
              <Button onClick={() => setConfirmOpen(false)} variant="outline" data-testid="export-clients-cancel">
                Annuler
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function NetworksPage() {
  const [nets, setNets] = useState([]);
  const [form, setForm] = useState({ slug: "", name: "", website: "", description: "", logo_url: "", phone: "", email: "", active: true });
  const [editingSlug, setEditingSlug] = useState(null);
  const [deleteNet, setDeleteNet] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  const fetch = () => api.get("/superadmin/networks").then((r) => setNets(r.data.networks || []));
  useEffect(() => { fetch(); }, []);

  const submit = async () => {
    if (!form.slug || !form.name) return toast.error("Nom et slug obligatoires");
    try {
      await api.put(`/superadmin/networks/${form.slug}`, form);
      toast.success(editingSlug ? "Réseau mis à jour" : "Réseau créé");
      setForm({ slug: "", name: "", website: "", description: "", logo_url: "", phone: "", email: "", active: true });
      setEditingSlug(null); fetch();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (n) => { setEditingSlug(n.slug); setForm({ ...n }); };

  const toggleActive = async (n) => {
    try {
      await api.put(`/superadmin/networks/${n.slug}`, { ...n, active: !n.active });
      fetch();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitDelete = async () => {
    if (!deleteReason.trim()) return toast.error("Motif obligatoire");
    try {
      const r = await api.delete(`/superadmin/networks/${deleteNet.slug}`, { data: { reason: deleteReason } });
      toast.success(`Réseau archivé (${r.data.deactivated_centers || 0} centres désactivés)`);
      setDeleteNet(null); setDeleteReason(""); fetch();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Réseaux</h1>
      <p className="text-slate-600 mt-2">Gérez les réseaux partenaires (création, modification, activation, archivage).</p>

      <div className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux grid md:grid-cols-2 gap-4">
        <div><Label>Slug (identifiant URL)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} disabled={!!editingSlug} className="mt-1 h-11" data-testid="net-slug" /></div>
        <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" data-testid="net-name" /></div>
        <div className="md:col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1" /></div>
        <div><Label>Site</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 h-11" /></div>
        <div><Label>Logo (URL)</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-1 h-11" /></div>
        <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" /></div>
        <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
        <div className="md:col-span-2 flex gap-2">
          <Button onClick={submit} className="bg-primary-blue text-white" data-testid="net-save">{editingSlug ? "Enregistrer" : "Créer le réseau"}</Button>
          {editingSlug && <Button variant="outline" onClick={() => { setEditingSlug(null); setForm({ slug: "", name: "", website: "", description: "", logo_url: "", phone: "", email: "", active: true }); }}>Annuler</Button>}
        </div>
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr><th className="text-left px-4 py-3">Nom</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Statut</th><th className="text-left px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {nets.map((n) => (
              <tr key={n.slug} className="border-t border-slate-100" data-testid={`net-row-${n.slug}`}>
                <td className="px-4 py-3">{n.name}</td>
                <td className="px-4 py-3 text-slate-500">{n.slug}</td>
                <td className="px-4 py-3">{n.active === false ? <Badge className="bg-slate-100 text-slate-500">Archivé</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>}</td>
                <td className="px-4 py-3 space-x-2">
                  <Button size="sm" variant="outline" onClick={() => edit(n)}>Modifier</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(n)}>{n.active === false ? "Réactiver" : "Désactiver"}</Button>
                  <Button size="sm" variant="outline" className="border-red-400 text-red-700" onClick={() => { setDeleteNet(n); setDeleteReason(""); }} data-testid={`net-delete-${n.slug}`}>Supprimer</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deleteNet && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-2xl max-w-md w-full">
            <h3 className="font-heading text-xl text-primary-blue">Archiver le réseau « {deleteNet.name} »</h3>
            <p className="text-sm text-slate-600 mt-2">L'historique est conservé. Les centres liés seront désactivés.</p>
            <Label className="text-sm mt-4 block">Motif obligatoire</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3} className="mt-1" data-testid="net-delete-reason" />
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeleteNet(null)}>Annuler</Button>
              <Button onClick={submitDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="net-delete-confirm">Confirmer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  const nav = useAdminNav();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  return (
    <DashboardLayout title="Administration" subtitle={isSuperAdmin ? "Super-admin" : "Admin"} nav={nav}>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="utilisateurs" element={<UsersPage />} />
        <Route path="centres" element={<Centers />} />
        <Route path="dossiers" element={<Dossiers />} />
        <Route path="faq" element={<FAQAdmin />} />
        <Route path="blog" element={<BlogAdmin />} />
        <Route path="rgpd" element={<RGPD />} />
        <Route path="logs" element={<Logs />} />
        {isSuperAdmin && <Route path="export-clients" element={<ExportClients />} />}
        {isSuperAdmin && <Route path="reseaux" element={<NetworksPage />} />}
      </Routes>
    </DashboardLayout>
  );
}
